// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import {
  ActionIcon,
  Box,
  Button,
  Center,
  Divider,
  em,
  Flex,
  Group,
  Menu,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure, useMediaQuery } from '@mantine/hooks';
import type { Filter, SearchRequest } from '@medplum/core';
import { getReferenceString, Operator, parseSearchRequest } from '@medplum/core';
import type { Communication, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { ThreadBucket } from '@polarissleep/messaging';
import {
  ChatList,
  markThreadRead,
  showErrorNotification,
  TenantScopedThreadChat,
  useThreadInbox,
} from '@polarissleep/messaging';
import {
  IconArrowLeft,
  IconChevronDown,
  IconMessageCircle,
  IconPlus,
  IconSearch,
  IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react';
import cx from 'clsx';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { getProviderAppEnvConfig } from '../../config/env';
import { NewTopicDialog } from './NewTopicDialog';
import { ParticipantFilter } from './ParticipantFilter';
import classes from './ThreadInbox.module.css';

interface ThreadInboxProps {
  query: string;
  threadId: string | undefined;
  bucket: ThreadBucket;
  subject?: Reference<Patient> | Patient | undefined;
  onNew: (message: Communication) => void;
  getThreadUri: (topic: Communication) => string;
  onChange: (search: SearchRequest) => void;
  unreadUri: string;
  sentUri: string;
  allUri: string;
}

export function ThreadInbox(props: ThreadInboxProps): JSX.Element {
  const { query, threadId, bucket, subject, onNew, getThreadUri, onChange, unreadUri, sentUri, allUri } = props;

  const medplum = useMedplum();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  const currentSearch = useMemo(() => parseSearchRequest(`Communication?${query}`), [query]);

  const searchParams = useMemo(() => new URLSearchParams(query), [query]);
  const itemsPerPage = Number.parseInt(searchParams.get('_count') || '20', 10);
  const currentOffset = Number.parseInt(searchParams.get('_offset') || '0', 10);
  const currentPage = Math.floor(currentOffset / itemsPerPage) + 1;

  // Extract participants from parsed search request filters (comma-separated)
  const selectedParticipants = useMemo((): Reference<Patient | Practitioner>[] => {
    const recipientFilters = currentSearch.filters?.filter((f) => f.code === 'recipient') ?? [];
    return recipientFilters.flatMap((f) =>
      f.value
        .split(',')
        .filter(Boolean)
        .map((ref) => ({ reference: ref }) as Reference<Patient | Practitioner>)
    );
  }, [currentSearch]);

  const {
    loading,
    error,
    threadMessages,
    selectedThread,
    total,
    currentUserRef,
    setSelectedThread,
    setThreadMessages,
    refreshThreadMessages,
  } = useThreadInbox({
    query,
    threadId,
    bucket,
  });

  const handleParticipantsChange = useCallback(
    (participants: Reference<Patient | Practitioner>[]) => {
      const otherFilters = currentSearch.filters?.filter((f) => f.code !== 'recipient') ?? [];
      const participantRefs = participants.map((p) => p.reference).filter(Boolean) as string[];
      const newFilters =
        participantRefs.length > 0
          ? [...otherFilters, { code: 'recipient', operator: Operator.EQUALS, value: participantRefs.join(',') }]
          : otherFilters;

      onChange({
        ...currentSearch,
        filters: newFilters,
        offset: 0,
      });
    },
    [currentSearch, onChange]
  );

  const sortDescending = useMemo(() => {
    const rule = currentSearch.sortRules?.find((r) => r.code === '_lastUpdated');
    // No rule present → server default is `-_lastUpdated` (desc).
    return rule ? Boolean(rule.descending) : true;
  }, [currentSearch]);

  const handleSortToggle = useCallback(() => {
    const otherRules = currentSearch.sortRules?.filter((r) => r.code !== '_lastUpdated') ?? [];
    onChange({
      ...currentSearch,
      sortRules: [...otherRules, { code: '_lastUpdated', descending: !sortDescending }],
      offset: 0,
    });
  }, [currentSearch, onChange, sortDescending]);

  const urlSearchState = useMemo<{ mode: 'topic' | 'patient'; value: string }>(() => {
    const filters = currentSearch.filters ?? [];
    const topicFilter = filters.find((f) => f.code === 'topic' || f.code === 'topic:text');
    if (topicFilter) {
      return { mode: 'topic', value: topicFilter.value };
    }
    const patientFilter = filters.find((f) => f.code === 'subject:Patient.name');
    if (patientFilter) {
      return { mode: 'patient', value: patientFilter.value };
    }
    return { mode: 'topic', value: '' };
  }, [currentSearch]);

  const [searchMode, setSearchMode] = useState<'topic' | 'patient'>(urlSearchState.mode);
  const [searchInput, setSearchInput] = useState<string>(urlSearchState.value);
  const [debouncedSearch] = useDebouncedValue(searchInput, 350);

  // Re-hydrate local state when the URL changes externally (back nav, bucket link, etc.).
  useEffect(() => {
    setSearchInput((prev) => (prev === urlSearchState.value ? prev : urlSearchState.value));
    setSearchMode((prev) => (urlSearchState.value === '' ? prev : urlSearchState.mode));
  }, [urlSearchState.mode, urlSearchState.value]);

  // Push debounced search input + mode into the FHIR query. Also strips any stale
  // `_has:Communication:part-of:_content` filter left over from earlier URLs.
  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    const existingFilters = currentSearch.filters ?? [];
    const otherFilters = existingFilters.filter(
      (f) =>
        f.code !== 'topic' &&
        f.code !== 'topic:text' &&
        f.code !== 'subject:Patient.name' &&
        f.code !== '_has:Communication:part-of:_content'
    );
    let searchFilter: Filter | undefined;
    if (trimmed) {
      searchFilter =
        searchMode === 'topic'
          ? { code: 'topic', operator: Operator.TEXT, value: trimmed }
          : { code: 'subject:Patient.name', operator: Operator.EQUALS, value: trimmed };
    }
    const newFilters = searchFilter ? [...otherFilters, searchFilter] : otherFilters;

    const sameLength = newFilters.length === existingFilters.length;
    const sameContent =
      sameLength &&
      newFilters.every((nf, i) => {
        const cf = existingFilters[i];
        return cf?.code === nf.code && cf?.operator === nf.operator && cf?.value === nf.value;
      });
    if (sameContent) {
      return;
    }

    onChange({ ...currentSearch, filters: newFilters, offset: 0 });
  }, [debouncedSearch, searchMode, currentSearch, onChange]);

  useEffect(() => {
    if (error) {
      showErrorNotification(error);
    }
  }, [error]);

  // When a thread is opened, mark its child messages as read for the current
  // user and refresh the list so the Unread bucket / sidebar badge update.
  useEffect(() => {
    if (!selectedThread?.id || !currentUserRef) {
      return;
    }
    const id = selectedThread.id;
    markThreadRead(medplum, id, currentUserRef)
      .then((markedCount) => {
        if (markedCount > 0) {
          refreshThreadMessages().catch((err: Error) => showErrorNotification(err));
        }
      })
      .catch((err: Error) => showErrorNotification(err));
  }, [selectedThread?.id, currentUserRef, medplum, refreshThreadMessages]);

  const handleThreadStatusChange = async (newStatus: Communication['status']): Promise<void> => {
    if (!selectedThread) {
      return;
    }
    try {
      const updatedThread = await medplum.updateResource({
        ...selectedThread,
        status: newStatus,
      });

      setSelectedThread(updatedThread);
      setThreadMessages((prev) =>
        prev.map(([parent, lastMsg]) => (parent.id === updatedThread.id ? [updatedThread, lastMsg] : [parent, lastMsg]))
      );
      await refreshThreadMessages();
    } catch (error) {
      showErrorNotification(error);
    }
  };

  const addThreadMessage = async (message: Communication): Promise<void> => {
    await refreshThreadMessages();
    setThreadMessages((prev) => [[message, undefined], ...prev]);
  };

  const handleNewTopicCompletion = (message: Communication): void => {
    // biome-ignore lint/complexity/noVoid: fire-and-forget with error handling via .catch()
    void addThreadMessage(message).catch((error) => showErrorNotification(error));
    onNew(message);
  };

  // md < 992px: single-panel mobile (list OR chat, toggled). md+ shows both.
  const isMobile = useMediaQuery(`(max-width: ${em(992)})`, false, { getInitialValueInEffect: false });
  const [mobilePanel, setMobilePanel] = useState<'list' | 'chat'>('list');

  useEffect(() => {
    if (isMobile && selectedThread) {
      setMobilePanel('chat');
    }
  }, [isMobile, selectedThread]);

  const showList = !isMobile || mobilePanel === 'list';
  const showChat = !isMobile || mobilePanel === 'chat';

  const skeletonTitleWidths = [80, 72, 68, 64];
  const skeletonSubtitleWidths = [85, 78, 70, 60];

  return (
    <>
      <div className={classes.container}>
        <Flex direction="row" h="100%" w="100%">
          {/* Left sidebar - Messages list */}
          {showList && (
            <Flex
              direction="column"
              w={{ base: '100%', md: 420 }}
              h="100%"
              className={showChat ? classes.rightBorder : undefined}
            >
              <Paper h="100%" style={{ display: 'flex', flexDirection: 'column' }}>
                <ScrollArea style={{ flex: 1 }} scrollbarSize={10} type="hover" scrollHideDelay={250}>
                  <Flex mih={64} align="center" justify="space-between" p="md" wrap="wrap" rowGap="xs" columnGap="xs">
                    <Group gap="xs">
                      <Button
                        component={Link}
                        to={unreadUri}
                        className={cx(classes.button, { [classes.selected]: bucket === 'unread' })}
                        size="compact-xs"
                        radius="xl"
                      >
                        Unread
                      </Button>
                      <Button
                        component={Link}
                        to={sentUri}
                        className={cx(classes.button, { [classes.selected]: bucket === 'sent' })}
                        size="compact-xs"
                        radius="xl"
                      >
                        Sent
                      </Button>
                      <Button
                        component={Link}
                        to={allUri}
                        className={cx(classes.button, { [classes.selected]: bucket === 'all' })}
                        size="compact-xs"
                        radius="xl"
                      >
                        All Messages
                      </Button>
                      <ParticipantFilter
                        selectedParticipants={selectedParticipants}
                        onFilterChange={handleParticipantsChange}
                      />
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                      <Select
                        size="xs"
                        w={100}
                        value={searchMode}
                        onChange={(value) => {
                          if (value === 'topic' || value === 'patient') {
                            setSearchMode(value);
                          }
                        }}
                        data={[
                          { value: 'topic', label: 'Topic' },
                          { value: 'patient', label: 'Patient' },
                        ]}
                        allowDeselect={false}
                        aria-label="Search field"
                      />
                      <TextInput
                        size="xs"
                        w={200}
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.currentTarget.value)}
                        leftSection={<IconSearch size={14} />}
                        placeholder={searchMode === 'topic' ? 'Search topic…' : 'Search patient…'}
                        aria-label={searchMode === 'topic' ? 'Search by topic title' : 'Search by patient name'}
                      />
                      <Tooltip label={sortDescending ? 'Sort oldest first' : 'Sort newest first'} withArrow>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={handleSortToggle}
                          aria-label={sortDescending ? 'Sort newest first' : 'Sort oldest first'}
                        >
                          {sortDescending ? <IconSortDescending size={18} /> : <IconSortAscending size={18} />}
                        </ActionIcon>
                      </Tooltip>
                      <ActionIcon radius="50%" variant="filled" color="polaris" onClick={openModal}>
                        <IconPlus size={16} />
                      </ActionIcon>
                    </Group>
                  </Flex>
                  <Divider />
                  {loading ? (
                    <Stack gap="md" p="md">
                      {Array.from({ length: 10 }).map((_, index) => {
                        const titleWidth = skeletonTitleWidths[index % skeletonTitleWidths.length];
                        const subtitleWidth = skeletonSubtitleWidths[index % skeletonSubtitleWidths.length];
                        return (
                          <Flex key={index} gap="sm" align="flex-start">
                            <Skeleton height={40} width={40} radius="50%" />
                            <Box style={{ flex: 1 }}>
                              <Flex direction="column" gap="xs">
                                <Skeleton height={16} width={`${titleWidth}%`} />
                                <Skeleton height={14} width={`${subtitleWidth}%`} />
                              </Flex>
                            </Box>
                          </Flex>
                        );
                      })}
                    </Stack>
                  ) : (
                    threadMessages.length > 0 && (
                      <ChatList
                        threads={threadMessages}
                        selectedCommunication={selectedThread}
                        getThreadUri={getThreadUri}
                        currentUserRef={currentUserRef}
                      />
                    )
                  )}
                  {threadMessages.length === 0 && !loading && <EmptyMessagesState />}
                </ScrollArea>
                {!loading && total !== undefined && total > itemsPerPage && (
                  <Box p="md">
                    <Center>
                      <Pagination
                        value={currentPage}
                        total={Math.ceil(total / itemsPerPage)}
                        onChange={(page) => {
                          const offset = (page - 1) * itemsPerPage;
                          onChange({
                            ...currentSearch,
                            offset,
                          });
                        }}
                        size="sm"
                        siblings={1}
                        boundaries={1}
                      />
                    </Center>
                  </Box>
                )}
              </Paper>
            </Flex>
          )}

          {/* Main chat area */}
          {showChat && selectedThread && (
            <Flex direction="column" style={{ flex: 1 }} h="100%">
              <Paper h="100%">
                <Stack h="100%" gap={0}>
                  <Flex h={64} align="center" justify="space-between" p="md">
                    <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                      <ActionIcon
                        hiddenFrom="md"
                        variant="subtle"
                        color="gray"
                        onClick={() => setMobilePanel('list')}
                        aria-label="Back to messages"
                      >
                        <IconArrowLeft size={20} />
                      </ActionIcon>
                      <Text fw={800} truncate fz="lg">
                        {selectedThread.topic?.text ?? 'Messages'}
                      </Text>
                    </Group>

                    <Menu position="bottom-end" shadow="md">
                      <Menu.Target>
                        <Button
                          variant="light"
                          color={getStatusColor(selectedThread.status)}
                          rightSection={
                            selectedThread.status === 'completed' ? undefined : <IconChevronDown size={16} />
                          }
                          radius="xl"
                          size="sm"
                        >
                          {selectedThread.status
                            .split('-')
                            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')}
                        </Button>
                      </Menu.Target>

                      {selectedThread.status !== 'completed' && (
                        <Menu.Dropdown>
                          <Menu.Item onClick={() => handleThreadStatusChange('completed')}>Completed</Menu.Item>
                        </Menu.Dropdown>
                      )}
                    </Menu>
                  </Flex>
                  <Divider />
                  <Flex direction="column" style={{ flex: 1 }} h="100%">
                    <TenantScopedThreadChat
                      key={`${getReferenceString(selectedThread)}`}
                      title={'Messages'}
                      thread={selectedThread}
                      excludeHeader={true}
                      organizationId={getProviderAppEnvConfig().medplumOrganizationId}
                    />
                  </Flex>
                </Stack>
              </Paper>
            </Flex>
          )}
          {showChat && !selectedThread && (
            <Flex direction="column" style={{ flex: 1 }} h="100%">
              <NoMessages />
            </Flex>
          )}
        </Flex>
      </div>

      <NewTopicDialog subject={subject} opened={modalOpened} onClose={closeModal} onSubmit={handleNewTopicCompletion} />
    </>
  );
}

function NoMessages(): JSX.Element {
  return (
    <Center h="100%" w="100%">
      <Stack align="center" gap="md">
        <ThemeIcon size={64} variant="light" color="gray">
          <IconMessageCircle size={32} />
        </ThemeIcon>
        <Stack align="center" gap="xs">
          <Text size="sm" c="dimmed" ta="center">
            Select a message from the list to view details
          </Text>
        </Stack>
      </Stack>
    </Center>
  );
}

function getStatusColor(status: Communication['status']): string {
  if (status === 'completed') {
    return 'green';
  }
  if (status === 'stopped') {
    return 'red';
  }
  return 'blue';
}

function EmptyMessagesState(): JSX.Element {
  return (
    <Flex direction="column" h="100%" justify="center" align="center">
      <Stack align="center" gap="md" pt="xl">
        <IconMessageCircle size={64} color="var(--mantine-color-gray-4)" />
        <Text size="lg" c="dimmed" fw={500}>
          No messages found
        </Text>
      </Stack>
    </Flex>
  );
}
